/** P125 (D22): bulk-move report rows. Pure logic, unit-tested so post-attempt
 * state can never rewrite a submitted attempt's outcomes: pre-exclusions come
 * from the send-time snapshot, and the attempt's own outcome record decides
 * moved/failed for everything that was actually submitted. */

export interface MoveAttemptLike {
  /** Bus command id from sendTracked ('sent' when the bridge has no id). */
  requestId: string;
  /** animalId -> reason, captured at send time (quarantined, already there...). */
  preExcluded: ReadonlyMap<string, string>;
}

export interface MoveOutcomeLike {
  requestId: string | null;
  moved: string[];
  failed: { animalId: string; reason: string }[];
}

export type MoveRowKind = 'not-moved' | 'sent' | 'moved' | 'failed';

export interface MoveRowStatus {
  animalId: string;
  kind: MoveRowKind;
  /** Pre-exclusion or failure reason; null for moved/sent. */
  reason: string | null;
}

/** Resolve the report row for every selected animal, attempt evidence first. */
export function resolveMoveRows(animalIds: string[], attempt: MoveAttemptLike, outcome: MoveOutcomeLike | null): MoveRowStatus[] {
  return animalIds.map((animalId) => {
    const pre = attempt.preExcluded.get(animalId);
    if (pre !== undefined) return { animalId, kind: 'not-moved', reason: pre };
    if (outcome === null) return { animalId, kind: 'sent', reason: null };
    if (outcome.moved.includes(animalId)) return { animalId, kind: 'moved', reason: null };
    const f = outcome.failed.find((x) => x.animalId === animalId);
    return { animalId, kind: 'failed', reason: f?.reason ?? null };
  });
}

/** The outcome log is bounded; when full, an absent id means the record aged
 * out - reported honestly instead of an indefinite wait or implied success. */
export const MOVE_OUTCOME_LOG_CAP = 20;

export function moveOutcomeAgedOut(requestId: string, outcomes: readonly MoveOutcomeLike[], cap: number = MOVE_OUTCOME_LOG_CAP): boolean {
  if (requestId === 'sent') return false;
  return outcomes.length >= cap && !outcomes.some((o) => o.requestId === requestId);
}
